import { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { canOfferWebPush, enableWebPush } from '../lib/push';

/**
 * The one-time "turn on notifications" card.
 *
 * Browsers only reliably show the notification-permission dialog from a USER
 * GESTURE (Safari: strictly; Chrome: an automatic ask gets the quiet bell or
 * nothing). The automatic enableWebPush() after login therefore stalls while
 * permission is still "default". This card closes that gap: it appears once,
 * bottom-right, and its "Turn on" CLICK carries the gesture - so the real
 * browser prompt is guaranteed to open.
 *
 * Shown only while it can actually help: signed in, push configured and
 * supported, permission still undecided, not previously dismissed.
 *   - Allow  -> token registers, the card never returns (permission granted).
 *   - Block  -> permission 'denied', canOfferWebPush() is false forever.
 *   - "Not now" -> remembered in localStorage; the Settings button remains.
 *   - Browser prompt dismissed (Esc) -> permission stays "default", so the
 *     card simply offers again on the next visit.
 */

const DISMISS_KEY = 'cgtodo.pushPromptDismissed';

export function EnablePushPrompt() {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) {
      setShow(false);
      return;
    }
    let dismissed = false;
    try {
      dismissed = localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      // Storage blocked (private mode) - just offer; worst case we ask again.
    }
    setShow(!dismissed && canOfferWebPush());
  }, [user]);

  if (!show) return null;

  async function turnOn() {
    setBusy(true);
    // This runs inside the click gesture -> the browser prompt WILL appear.
    await enableWebPush();
    setBusy(false);
    // Whatever was answered, the ask is over for now. If the person only
    // dismissed the browser prompt, permission stays "default" and the card
    // offers again next visit.
    setShow(false);
  }

  function notNow() {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Not persistable - the card will offer again next visit. Fine.
    }
    setShow(false);
  }

  return (
    <div
      role="dialog"
      aria-label="Turn on notifications"
      className="pointer-events-auto rounded-xl border border-slate-200 bg-white p-4 shadow-xl ring-1 ring-black/5"
    >
      <div className="flex items-start gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand-100 text-brand-700">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true" className="h-4 w-4">
            <path
              d="M10 2.5a4.5 4.5 0 0 0-4.5 4.5c0 3.2-1 4.4-1.7 5.1a.6.6 0 0 0 .45 1h11.5a.6.6 0 0 0 .45-1c-.7-.7-1.7-1.9-1.7-5.1A4.5 4.5 0 0 0 10 2.5ZM8.2 15a1.9 1.9 0 0 0 3.6 0"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Get task alerts on this device</p>
          <p className="mt-0.5 text-xs text-slate-500">
            See new assignments and reminders even when the portal tab is closed.
          </p>
        </div>
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={notNow}
          className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100"
        >
          Not now
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => void turnOn()}
          className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-700 disabled:opacity-50"
        >
          {busy ? 'Asking…' : 'Turn on'}
        </button>
      </div>
    </div>
  );
}
